import React from 'react';

import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useAuth, useMounted } from 'hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { Routes } from 'routes';

import { getGameProviderShortcode, isExternalGameProvider } from 'utils/game.utils';

import { Game, SubscriptionType } from 'services/api/definitions/game.definitions';

import Link from 'components/@navigation/Link';

interface Props {
  game: Game;
  isLoadingSubscriptions: boolean;
  isLoadingUrl: boolean;
  isErrorUrl: boolean;
  isSubscribedTo: (shortCode: string) => boolean | undefined;
  handlePlayClick: () => void;
}

const GameDetailButtons = ({
  game,
  isLoadingUrl,
  isErrorUrl,
  isLoadingSubscriptions,
  isSubscribedTo,
  handlePlayClick,
}: Props) => {
  const isMounted = useMounted();
  const { isLoggedIn } = useAuth();
  const intl = useIntl();

  if (isExternalGameProvider(game.provider)) {
    const providerShortCode = getGameProviderShortcode(game.provider);
    if (isLoadingSubscriptions || !isMounted) {
      return <Skeleton width="100%" height={42} variant="rect" />;
    } else if (providerShortCode && isSubscribedTo(providerShortCode)) {
      return (
        <>
          <Button
            component={Link}
            href={game.url}
            variant="contained"
            color="primary"
            fullWidth
            disabled={!isLoggedIn}
          >
            <FormattedMessage id="game.details.actions.view" />
          </Button>
          <Box mt={2}>
            <Button
              component={Link}
              href={Routes.subscriptions}
              variant="outlined"
              color="primary"
              fullWidth
              disabled={!isLoggedIn}
            >
              <FormattedMessage id="game.details.actions.manage_subscription" />
            </Button>
          </Box>
        </>
      );
    } else if (providerShortCode) {
      return (
        <>
          <Box mb={2}>
            <Button
              component={Link}
              href={Routes.subscriptions}
              variant="contained"
              color="primary"
              fullWidth
              disabled={!isLoggedIn}
            >
              <FormattedMessage
                id="game.details.actions.play"
                values={{
                  amount:
                    game.price.toLowerCase() === 'free'
                      ? intl.formatMessage({ id: 'game.details.price.free' })
                      : game.price,
                }}
              />
            </Button>
          </Box>
          <Box width="100%" textAlign="center">
            <Typography variant="body2" color="textSecondary">
              <FormattedMessage
                id={
                  // TODO: check all possibilities
                  game.subscriptionType === SubscriptionType.oneTimePurchase
                    ? 'game.explainer_text.one_time_pay'
                    : 'game.explainer_text.per_game_session'
                }
              />
            </Typography>
          </Box>
        </>
      );
    } else {
      return null;
    }
  } else {
    return (
      <>
        <Box my={2}>
          <Button
            onClick={handlePlayClick}
            variant="contained"
            color="primary"
            fullWidth
            disabled={!isLoggedIn || isErrorUrl || isLoadingUrl}
          >
            {isLoadingUrl ? (
              <CircularProgress size={24} />
            ) : (
              <FormattedMessage
                id="game.details.actions.play"
                values={{
                  amount:
                    game.price.toLowerCase() === 'free'
                      ? intl.formatMessage({ id: 'game.details.price.free' })
                      : game.price,
                }}
              />
            )}
          </Button>
        </Box>
        <Box width="100%" textAlign="center">
          <Typography variant="body2" color="textSecondary">
            <FormattedMessage
              id={
                // TODO: check all possibilities
                game.subscriptionType === SubscriptionType.oneTimePurchase
                  ? 'game.explainer_text.one_time_pay'
                  : 'game.explainer_text.per_game_session'
              }
            />
          </Typography>
        </Box>
      </>
    );
  }
};

export default GameDetailButtons;
